import { useMemo } from 'react';
import {
  useGetListAccessibleConsensusRules,
  useGetListApprovalActions,
  useGetWorkspaceMembers,
  useRefreshListAccessibleConsensusRules,
} from '@cbhq/identity-api-hooks/account-management/fetch';

import { WORKSPACE_ORG_ADMIN } from '../../../constants/roles';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useScopedOrganizationId } from '../../../hooks/useScopedOrganizationId';
import { normalizeWorkspaceMember } from '../../Team/utils/workspaceTeam';
import { buildApprovalProducts } from '../utils/approvalRulesDisplay';
import { ORG_SCOPED_RESOURCE_TYPE } from '../utils/constants';

type UseApprovalRulesDataParams = {
  legalEntityId: string;
};

/**
 * Data hook for the Approval rules page. Fetches rules merged server-side
 * across all scopes accessible within the selected organization.
 */
export function useApprovalRulesData({ legalEntityId }: UseApprovalRulesDataParams) {
  const { selectedOrganization } = useOrganization();

  const organizationId = useScopedOrganizationId();
  const isOrgAdmin = (selectedOrganization?.roles ?? []).includes(WORKSPACE_ORG_ADMIN);

  const skip = organizationId.length === 0;

  const approvalActions = useGetListApprovalActions({ skip });
  const accessibleRules = useGetListAccessibleConsensusRules({ organizationId }, { skip });
  const workspaceMembers = useGetWorkspaceMembers({ organizationId }, { skip });
  const refresh = useRefreshListAccessibleConsensusRules({ organizationId });

  const members = useMemo(
    () => workspaceMembers?.members?.map(normalizeWorkspaceMember),
    [workspaceMembers],
  );

  const approvalProducts = useMemo(
    () =>
      buildApprovalProducts({
        actions: approvalActions?.actions,
        rules: accessibleRules?.rules ?? [],
        legalEntityId,
        organizationId,
        excludeResourceTypes: isOrgAdmin ? undefined : [ORG_SCOPED_RESOURCE_TYPE],
      }),
    [approvalActions, accessibleRules, isOrgAdmin, legalEntityId, organizationId],
  );

  return { approvalProducts, members, refresh };
}
